<template>
  <div class="row g-4">
    <div class="col-12">
      <div
        class="d-flex flex-wrap justify-content-between align-items-center gap-2"
      >
        <div>
          <h5 class="mb-0">Net revenue retention</h5>
          <small class="text-muted">
            How much fee revenue last period's customers generate this period.
            Above 100% means the existing base grows on its own.
          </small>
        </div>
        <div class="d-flex align-items-center gap-3">
          <div
            class="btn-group btn-group-sm"
            role="group"
            aria-label="Retention window"
          >
            <button
              v-for="option in windowOptions"
              :key="option"
              type="button"
              class="btn"
              :class="
                option === windowMonths ? 'btn-primary' : 'btn-outline-primary'
              "
              @click="windowMonths = option"
            >
              {{ option }}m
            </button>
          </div>
          <MethodologyModal title="NRR methodology">
            <p>
              For every calendar month <em>M</em> and the chosen window
              <em>W</em> (months):
            </p>
            <pre class="bg-light p-2 rounded small mb-3">
cohort = customers with fee revenue in the prior window (M − 2W, M − W]
NRR(M) = cohort revenue in the current window (M − W, M]  /  cohort revenue in the prior window
GRR(M) = same, but each customer's current revenue is capped at its prior revenue</pre>
            <dl>
              <dt>Customer</dt>
              <dd>
                Any company invoiced a Soldera fee. Producers pay commission and
                platform fees; buyers pay procurement fees.
              </dd>
              <dt>Customer revenue</dt>
              <dd>
                Soldera fee lines on <em>paid</em> fee invoices, attributed to
                the company on the invoice and to the month the invoice was
                paid: <code>{{ revenueTypes.join(", ") }}</code
                >.
              </dd>
              <dt>Excluded</dt>
              <dd>
                GO trading margin (no customer invoice line), referral payouts,
                registry pass-through fees, buyer GO invoices (pass-through
                money, not Soldera revenue).
              </dd>
              <dt>Expansion / contraction / churn</dt>
              <dd>
                Per cohort customer: revenue above prior is expansion, below
                prior is contraction, zero is churn. Prior + expansion −
                contraction − churn = current, so the bridge always reconciles.
              </dd>
              <dt>New customers</dt>
              <dd>
                No revenue in the prior window. Reported separately; they never
                enter NRR.
              </dd>
            </dl>
            <p class="mb-1">
              <strong>Window.</strong> 12 months compares trailing-twelve-month
              revenue against the twelve months before it, which smooths
              seasonal GO settlement. Shorter windows react faster but a
              customer that simply settles in a different quarter will look
              churned.
            </p>
            <p class="mb-0 text-muted small">
              Backend:
              <code>app/money/admin/revenue_retention_service.py</code>.
            </p>
          </MethodologyModal>
        </div>
      </div>
    </div>

    <LoadingIndicatorComponent
      v-if="isLoading"
      message="Calculating retention..."
    />

    <template v-else-if="data && latest">
      <div class="col-12">
        <div class="row g-3">
          <div class="col-md-6 col-lg-3">
            <div
              class="card h-100"
              :class="
                latest.nrr === null
                  ? 'border-secondary'
                  : latest.nrr >= 1
                    ? 'border-success'
                    : 'border-warning'
              "
            >
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">
                  NRR · {{ periodLabel(latest.year, latest.month) }}
                </h6>
                <h3
                  class="card-title mb-0"
                  :class="
                    latest.nrr === null
                      ? 'text-secondary'
                      : latest.nrr >= 1
                        ? 'text-success'
                        : 'text-warning-emphasis'
                  "
                >
                  {{ formatRatio(latest.nrr) }}
                </h3>
                <small class="text-muted"
                  >{{ latest.window_months }}-month window ·
                  {{ latest.cohort_size }} customers in cohort</small
                >
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 border-info">
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">
                  GRR (gross retention)
                </h6>
                <h3 class="card-title text-info mb-0">
                  {{ formatRatio(latest.grr) }}
                </h3>
                <small class="text-muted"
                  >Retention without expansion credit</small
                >
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <button
              type="button"
              class="card h-100 w-100 text-start border-danger kpi-link"
              @click="$emit('openMonth', latest, ['churned'])"
            >
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">
                  Churn
                  <font-awesome-icon
                    icon="arrow-up-right-from-square"
                    class="small ms-1"
                  />
                </h6>
                <h3 class="card-title text-danger mb-0">
                  {{ formatMoney(latest.churn) }} €
                  <span
                    v-if="latest.prior_revenue > 0"
                    class="fs-6 text-muted fw-normal"
                    >({{ pct(latest.churn, latest.prior_revenue) }}%)</span
                  >
                </h3>
                <small class="text-muted"
                  >{{ latest.churned_customers }} customers with no revenue this
                  window. Click to see who.</small
                >
              </div>
            </button>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 border-primary">
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">New customers</h6>
                <h3 class="card-title text-primary mb-0">
                  {{ latest.new_customers }}
                </h3>
                <small class="text-muted"
                  >{{ formatMoney(latest.new_revenue) }} € revenue, not in
                  NRR</small
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-7">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">NRR and GRR by month</h5>
            <p class="text-muted small mb-2">
              Click a point to see the customers behind it.
            </p>
            <div class="chart-lg">
              <canvas ref="ratioChartRef"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-5">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Revenue bridge by month</h5>
            <p class="text-muted small mb-2">
              Prior cohort revenue → expansion, contraction, churn, new.
            </p>
            <div class="chart-lg">
              <canvas ref="bridgeChartRef"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Retention table</h5>
            <p class="text-muted small">
              Click a row for the per-customer breakdown.
            </p>
            <DataTable
              :value="tableRows"
              sortField="period"
              :sortOrder="-1"
              class="p-datatable-sm border rounded"
              :rowClass="clickableRow"
              @row-click="(event) => $emit('openMonth', event.data.source)"
            >
              <Column field="period" header="Period" sortable />
              <Column field="cohort_size" header="Cohort" sortable />
              <Column field="prior_revenue" header="Prior revenue" sortable>
                <template #body="{ data }"
                  >{{ formatMoney(data.prior_revenue) }} €</template
                >
              </Column>
              <Column field="expansion" header="Expansion" sortable>
                <template #body="{ data }"
                  ><span class="text-success"
                    >+{{ formatMoney(data.expansion) }} €</span
                  ></template
                >
              </Column>
              <Column field="contraction" header="Contraction" sortable>
                <template #body="{ data }"
                  ><span class="text-warning-emphasis"
                    >-{{ formatMoney(data.contraction) }} €</span
                  ></template
                >
              </Column>
              <Column field="churn" header="Churn" sortable>
                <template #body="{ data }">
                  <span class="text-danger"
                    >-{{ formatMoney(data.churn) }} €</span
                  >
                  <small class="text-muted ms-1"
                    >({{ data.churned_customers }})</small
                  >
                </template>
              </Column>
              <Column field="current_revenue" header="Current revenue" sortable>
                <template #body="{ data }"
                  ><span class="fw-semibold"
                    >{{ formatMoney(data.current_revenue) }} €</span
                  ></template
                >
              </Column>
              <Column field="nrr" header="NRR" sortable>
                <template #body="{ data }">
                  <span
                    class="fw-bold"
                    :class="
                      data.nrr === null
                        ? 'text-muted'
                        : data.nrr >= 1
                          ? 'text-success'
                          : 'text-warning-emphasis'
                    "
                  >
                    {{ formatRatio(data.nrr) }}
                  </span>
                </template>
              </Column>
              <Column field="grr" header="GRR" sortable>
                <template #body="{ data }">{{
                  formatRatio(data.grr)
                }}</template>
              </Column>
              <Column field="new_customers" header="New" sortable>
                <template #body="{ data }">
                  {{ data.new_customers }}
                  <small class="text-muted ms-1"
                    >{{ formatMoney(data.new_revenue) }} €</small
                  >
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="col-12 text-muted">
      No retention data for this range.
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatMoney } from "@/composables/formatUtils";
import type { ChartConfiguration } from "chart.js";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, nextTick, useTemplateRef, watch } from "vue";
import MethodologyModal from "./MethodologyModal.vue";
import { formatRatio, pct, periodLabel } from "./financialDashboardMetrics";
import type {
  RetentionCustomerStatus,
  RetentionResponse,
  RetentionRow,
} from "./financialDashboardTypes";
import { useChartCanvas } from "./useChartCanvas";

interface RetentionTableRow extends RetentionRow {
  period: string;
  source: RetentionRow;
}

const props = defineProps<{
  data: RetentionResponse | null;
  isLoading: boolean;
}>();

// Stable reference: an inline arrow in the template is a new prop every render and makes
// PrimeVue's DataTable re-render itself endlessly.
const clickableRow = () => "cursor-pointer";

const emit = defineEmits<{
  openMonth: [row: RetentionRow, statuses?: RetentionCustomerStatus[]];
}>();

/** Trailing window in months; the parent refetches when it changes. */
const windowMonths = defineModel<number>("windowMonths", { required: true });

const ratioChartRef = useTemplateRef<HTMLCanvasElement>("ratioChartRef");
const bridgeChartRef = useTemplateRef<HTMLCanvasElement>("bridgeChartRef");
const ratioChart = useChartCanvas(ratioChartRef);
const bridgeChart = useChartCanvas(bridgeChartRef);

const windowOptions = computed(
  () => props.data?.available_windows ?? [12, 6, 3]
);
const revenueTypes = computed(() => props.data?.revenue_item_types ?? []);
const rows = computed(() => props.data?.rows ?? []);
const latest = computed(() => rows.value.at(-1) ?? null);

const tableRows = computed<RetentionTableRow[]>(() =>
  rows.value.map((row) => ({
    ...row,
    period: periodLabel(row.year, row.month),
    source: row,
  }))
);

const toPercent = (ratio: number | null): number | null =>
  ratio === null ? null : ratio * 100;

function ratioChartConfig(): ChartConfiguration {
  const labels = rows.value.map((row) => periodLabel(row.year, row.month));
  return {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: "NRR",
          data: rows.value.map((row) => toPercent(row.nrr)),
          borderColor: "#198754",
          backgroundColor: "#198754",
          tension: 0.25,
          pointRadius: 4,
        },
        {
          label: "GRR",
          data: rows.value.map((row) => toPercent(row.grr)),
          borderColor: "#0dcaf0",
          backgroundColor: "#0dcaf0",
          tension: 0.25,
          pointRadius: 3,
        },
        {
          label: "100%",
          data: rows.value.map(() => 100),
          borderColor: "rgba(108, 117, 125, 0.6)",
          borderDash: [6, 4],
          pointRadius: 0,
          borderWidth: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: { y: { title: { display: true, text: "%" }, beginAtZero: true } },
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toFixed(1)}%`,
          },
        },
      },
      onClick: (_event, elements) => {
        if (elements.length > 0)
          emit("openMonth", rows.value[elements[0].index]);
      },
    },
  };
}

function bridgeChartConfig(): ChartConfiguration {
  const labels = rows.value.map((row) => periodLabel(row.year, row.month));
  return {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Retained (capped)",
          data: rows.value.map(
            (row) => row.prior_revenue - row.churn - row.contraction
          ),
          backgroundColor: "rgba(13, 110, 253, 0.7)",
          stack: "bridge",
        },
        {
          label: "Expansion",
          data: rows.value.map((row) => row.expansion),
          backgroundColor: "rgba(25, 135, 84, 0.7)",
          stack: "bridge",
        },
        {
          label: "New customers",
          data: rows.value.map((row) => row.new_revenue),
          backgroundColor: "rgba(111, 66, 193, 0.6)",
          stack: "bridge",
        },
        {
          label: "Contraction",
          data: rows.value.map((row) => -row.contraction),
          backgroundColor: "rgba(255, 193, 7, 0.7)",
          stack: "bridge",
        },
        {
          label: "Churn",
          data: rows.value.map((row) => -row.churn),
          backgroundColor: "rgba(220, 53, 69, 0.7)",
          stack: "bridge",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "EUR" } },
      },
      plugins: {
        legend: { position: "top", labels: { boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y ?? 0)} €`,
          },
        },
      },
      onClick: (_event, elements) => {
        if (elements.length > 0)
          emit("openMonth", rows.value[elements[0].index]);
      },
    },
  };
}

async function renderCharts() {
  await nextTick();
  if (!rows.value.length) return;
  ratioChart.render(ratioChartConfig());
  bridgeChart.render(bridgeChartConfig());
}

watch(() => [props.data, props.isLoading], renderCharts, {
  immediate: true,
  flush: "post",
});
</script>

<style scoped lang="scss">
.chart-lg {
  height: 340px;
}

.kpi-link {
  background: var(--bs-card-bg, #fff);
  transition: box-shadow 0.15s ease-in-out;

  &:hover {
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.08);
  }
}

:deep(.cursor-pointer) {
  cursor: pointer;
}
</style>
